import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {IReduxState} from "reducers";
import {connect} from "react-redux";
import {TextField} from "components/common/inputFunctions";
import {
    EGoldField, HeaderTitle,
    initialValidationStatus,
    renderProceed, roundDown, roundWithUpperValue,
    validateBuyGold,
} from "egold/utils";
import {imageBaseUrl} from "config";
import {IBuyPrice, IEgoldUserDetails, IValidation, IVerifyGoldPrice} from "StateInterface";
import {RouteComponentProps} from "react-router-dom";
import EGoldHeaderNav from "egold/components/header";
import Timer from "egold/components/timer";
import {getBuyPrice, hideRegisterModal, showRegisterModal, verifyBuyPrice} from "egold/actions";
import RegisterUser from "egold/components/registerUser";
import {vendor} from "egold/egold-config";
import {indexPath, routePaths} from "egold/routes";
import KycValidation from "egold/kyc";
import {EVENT_NAMES} from "Dashboard/utils";

interface IBuyGoldInput {
    inRupees: string;
    inGrams: string;
}
const initialUserInput = {
    inRupees: "",
    inGrams: "",
};
interface IConnectProps {
    dispatch?: (action: any) => void;
    buyPrice: IBuyPrice;
    emailId: string;
    userDetails: IEgoldUserDetails;
    userId: number;
    verifyBuyPrice: IVerifyGoldPrice;
    isRegisterModalOpen: boolean;
    isAndroid: boolean;
}

let availableTime = 0;
export const minGoldPurchaseAmount = 100;
const minAmountForKyc = 500;

function BuyGold(props: IConnectProps & RouteComponentProps) {
    const timerRef: any = useRef();
    const [userInput, updateUserInput] = useState<IBuyGoldInput>(initialUserInput);
    const [validationStatus, updateValidationStatus] = useState<IValidation>(initialValidationStatus);
    const [kycRequired, setKycDialog] = useState<boolean>(false);
    const {current_price, rate_validity, applicable_tax} = props.buyPrice;
    useEffect(() => {
        props.dispatch(getBuyPrice());
    }, []);
    const changeAvailableTime = (time: number) => availableTime = time;
    function handleChange(fieldName: EGoldField, value: number | string) {
        const gst = roundWithUpperValue((current_price * applicable_tax / 100), 2);
        const currentPriceWithGst = Number((current_price + gst).toFixed(2));
        if (fieldName === EGoldField.RUPEES) {
            const gramValue = (Number(value) / currentPriceWithGst);
            updateUserInput((prevState: any) => {
                return{
                    ...prevState,
                    inRupees: value,
                    inGrams: roundDown(gramValue, 4),
                };
            });
        } else if (fieldName === EGoldField.GRAMS) {
            const rupeesValue = Number((Number(value) * currentPriceWithGst));
            updateUserInput((prevState: any) => {
                return{
                    ...prevState,
                    inGrams: value,
                    inRupees: rupeesValue.toFixed(2),
                };
            });
        }
    }
    function proceedToConfirm(price, goldAmount) {
        const goldInfo = {
            rate_id: props.buyPrice.rate_id,
            vendor_id: props.userDetails.vendor_id,
            gold_amount: goldAmount,
            buy_price: Number(price.toFixed(2)),
        };
        props.dispatch(verifyBuyPrice(vendor.SAFEGOLD, goldInfo));
        props.history.push({pathname: `${indexPath}${routePaths.CONFIRM_BUY_PAYMENT}`,
            state: {timer: availableTime}});
    }
    function handleProceed() {
        const {inRupees, inGrams} = userInput;
        const validation = validateBuyGold(inRupees, inGrams);
        if (validation.status) {
            if (props.userDetails) {
                if ((!props.userDetails.panDetails || (props.userDetails.panDetails && Object.entries(props.userDetails.panDetails).length === 0)) && Number(inRupees) >= minAmountForKyc) {
                    setKycDialog(true);
                } else {
                    proceedToConfirm(Number(inRupees), Number(inGrams));
                }
            } else {
                props.dispatch(showRegisterModal());
            }
        }
        updateValidationStatus(validation);
    }
    const onKycClose = () => {
        setKycDialog(false);
    };
    function renderTextFields() {
        return(
            <div className="text-field-container d-inline-flex my-3 mx-2">
                <div className="position-relative">
                    <TextField label="Buy in Rupees"
                               value={userInput.inRupees}
                               fieldName={EGoldField.RUPEES}
                               onChange={handleChange}
                               type="number"
                               error={validationStatus.type === EGoldField.RUPEES ? validationStatus.msg : ""}
                    />
                    <span className="left-text">Min {minGoldPurchaseAmount}</span>
                </div>
                <div className="equal-sign"> = </div>
                <div className="position-relative">
                    <TextField label="Buy in Grams"
                               value={userInput.inGrams}
                               fieldName={EGoldField.GRAMS}
                               onChange={handleChange}
                               type="number"
                               error={validationStatus.type === EGoldField.GRAMS ? validationStatus.msg : ""}
                    />
                    <span className="right-text">Max 30gm(s)</span>
                </div>
            </div>
        );
    }
    function renderTimer() {
        return(
            <div className="text-center" style={{fontSize: "14px", color: "#000000"}}>
                Price valid for&nbsp;
                <Timer seconds={300}
                       handleTrackTime={changeAvailableTime}
                       ref={timerRef}
                       handleTimeEnd={() => restartTimer(300)}
                />
            </div>
        );
    }
    function restartTimer(time) {
        props.dispatch(getBuyPrice());
        timerRef.current.reStartTimer(time);
    }
    return (
        <div className="add-margin">
            <EGoldHeaderNav title={HeaderTitle.BUY}/>
            {
                    kycRequired &&
                    <KycValidation
                        active={kycRequired}
                        handleClose={onKycClose}
                        handleProceed={handleProceed}
                    />
            }
            <div className="buy-gold-container mt-4">
                {renderGoldBrand(current_price)}
                {renderTextFields()}
                <div className="kyc-security-text">
                    * For security reasons, to buy more than ₹500 worth of gold we will need to verify your PAN details.
                </div>
                {renderProceed(`Proceed to Pay ${userInput.inRupees}`, handleProceed)}
                {renderTimer()}
                <RegisterUser active={props.isRegisterModalOpen}
                              handleClose={() => props.dispatch(hideRegisterModal())}
                />
            </div>
        </div>
    );
}

function renderGoldBrand(currentPrice: number) {
    return(
        <div>
            <div className={"buygold-brand-container mt-4 mx-2"}>
                <div className="left">
                    <img src={`${imageBaseUrl}/img/icons/eGold/safegold_logo.svg`} style={{width: "85px"}}/>
                    <br/>
                    <i className="fa fa-inr price-icon" aria-hidden="true"/>&nbsp;
                    <b className="left-text">{currentPrice}/gm</b>
                </div>
                <div className="right">
                    <i className="fa fa-check-circle float-right" style={{color: "green"}} aria-hidden="true"/>
                    <br/>
                    <p className="right-text p-0 m-0">24k 99.5% Purity</p>
                </div>
            </div>
            <div className="bottom-gst-text">*Exclusive of 3% GST</div>
        </div>
    );
}

function renderFaq() {
    return(
        <div className="faq-section border-top border-bottom d-inline-flex align-items-center pl-3 w-100">
            <img src={`${imageBaseUrl}/img/icons/eGold/FAQ.png`} style={{width: "50px"}}/>
            <b>FAQs</b>
            <span className="ml-auto pr-4">
                <img src={`${imageBaseUrl}/img/icons/eGold/next.png`} style={{width: "20px"}}/>
            </span>
        </div>
    );
}

const mapStateToProps = (state: IReduxState): IConnectProps => {
    return {
        buyPrice: state.eGold.buyPrice,
        emailId: state.login.auth.email,
        userDetails: state.eGold.eGoldUserDetails,
        userId: state.login.auth.userId,
        verifyBuyPrice: state.eGold.verifyBuyPrice,
        isRegisterModalOpen: state.eGold.isRegisterModalOpen,
        isAndroid: state.login.isAndroid,
    };
};

export default connect<IConnectProps, {}, {}>(mapStateToProps)(BuyGold);
